import React from "react";
import fscreen from "fscreen";

// Detect whether we're embedded in an iframe (e.g. analytics inside the ATS). A
// cross-origin parent throws on access, which also means we're framed.
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

// Native fullscreen is unavailable (or actively rejected with "Permissions check
// failed") when the document is embedded in an iframe without a fullscreen permission
// policy. In that case we never call requestFullscreen — presentation mode is driven
// purely by the `enabled` prop / CSS — and the caller is responsible for providing an
// in-page way to exit (Escape handler / exit button).
const canUseNativeFullscreen = !isInIframe;

/**
 * Drop-in replacement for the unmaintained `react-full-screen` (0.2.5) used by
 * presentation mode. The original calls `fscreen.requestFullscreen()` without catching
 * the returned promise, so a rejected request surfaces as an uncaught promise rejection
 * in the console. This version swallows that rejection and avoids native fullscreen
 * entirely inside an iframe.
 *
 * API matches the subset we use: `enabled` (bool) and `onChange(isFullscreen)`.
 */
const FullScreen = ({enabled, onChange, children}) => {
  const nodeRef = React.useRef(null);
  const wasFullscreenRef = React.useRef(false);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    if (!canUseNativeFullscreen) {
      return undefined;
    }
    const handleChange = () => {
      if (fscreen.fullscreenElement === nodeRef.current) {
        wasFullscreenRef.current = true;
        if (onChangeRef.current) onChangeRef.current(true);
      } else if (!fscreen.fullscreenElement && wasFullscreenRef.current) {
        wasFullscreenRef.current = false;
        if (onChangeRef.current) onChangeRef.current(false);
      }
    };
    fscreen.addEventListener("fullscreenchange", handleChange);
    return () => fscreen.removeEventListener("fullscreenchange", handleChange);
  }, []);

  React.useEffect(() => {
    if (!canUseNativeFullscreen || !fscreen.fullscreenEnabled) {
      return;
    }
    const node = nodeRef.current;
    const isNativeFullscreen = fscreen.fullscreenElement === node;
    if (enabled && !isNativeFullscreen) {
      Promise.resolve(fscreen.requestFullscreen(node)).catch(() => {
        // Request can be rejected (e.g. permissions policy); presentation mode still
        // renders via CSS so there's nothing to recover here.
      });
    } else if (!enabled && isNativeFullscreen) {
      Promise.resolve(fscreen.exitFullscreen()).catch(() => {});
    }
  }, [enabled]);

  return (
      <div
          ref={nodeRef}
          className={enabled ? "fullscreen fullscreen-enabled" : "fullscreen"}
          style={enabled ? {height: "100%", width: "100%"} : undefined}>
        {children}
      </div>
  );
};

export default FullScreen;
